/**
 * Environment utility functions for detecting and displaying environment info
 */

import type { PillColor } from '@wasabi/actions'

export type Environment = 'DEV' | 'STAGING' | 'PROD'

export type BannerColor = 'success' | 'warning' | 'error'

/**
 * Get the current environment based on Vite build mode
 * @returns The environment name (DEV, STAGING, or PROD)
 */
export const getEnvironment = (): Environment => {
  const mode = import.meta.env.MODE

  if (mode === 'production') {
    return 'PROD'
  }

  if (mode === 'staging') {
    return 'STAGING'
  }

  // Default to DEV for development mode and localhost
  return 'DEV'
}

/**
 * Check if the current environment is production
 * @returns True if running in production
 */
export const isProduction = (): boolean => {
  return getEnvironment() === 'PROD'
}

/**
 * Per-environment colours for the surfaces that must be unmistakable about which environment you
 * are looking at. Two names for one colour because the consumers speak different vocabularies:
 * Vuetify components take theme names, Wasabi's WPills takes its own palette names. Keeping both
 * in one table is what stops them drifting apart into a red banner with a green pill.
 *
 * Independent of the user's Display Settings colour preference on purpose: that preference can be
 * switched off entirely, and a signal you are relying on to notice you are in production must not
 * be something a user can silently turn off.
 */
const ENVIRONMENT_COLORS: Record<Environment, { banner: BannerColor; pill: PillColor }> = {
  DEV: { banner: 'success', pill: 'green' },
  STAGING: { banner: 'warning', pill: 'yellow' },
  PROD: { banner: 'error', pill: 'red' }
}

/**
 * Every build mode we actually ship or run under: the three in package.json plus Vitest's own.
 * 'test' is listed so a unit run doesn't trip the unrecognized-mode path below.
 */
const RECOGNIZED_MODES = ['production', 'staging', 'development', 'test']

/**
 * Whether the build mode is one we recognize at all.
 *
 * This matters because getEnvironment() collapses anything unmatched into 'DEV' — sensible for a
 * tab-title prefix, dangerous for a safety signal. amplify.yml selects the build mode by BRANCH
 * NAME (main -> production, staging|release/* -> staging, everything else -> dev), so a renamed
 * branch or a new deploy target silently produces a dev build. Left alone, the banner would then
 * render green — the calmest colour — on a real environment, which is worse than showing nothing.
 *
 * So the colour helpers below fail toward alarm rather than calm, without changing what
 * getEnvironment() means for its other callers.
 */
export const isRecognizedMode = (): boolean => RECOGNIZED_MODES.includes(import.meta.env.MODE)

/**
 * Vuetify theme colour for the current environment. Always paired with the environment NAME
 * wherever it is used — colour alone is not a signal every reader can use.
 */
export const getEnvironmentBannerColor = (): BannerColor => {
  if (!isRecognizedMode()) return ENVIRONMENT_COLORS.PROD.banner
  return ENVIRONMENT_COLORS[getEnvironment()].banner
}

/**
 * WPills palette colour for the current environment — the label rendered next to the banner colour.
 */
export const getEnvironmentPillColor = (): PillColor => {
  if (!isRecognizedMode()) return ENVIRONMENT_COLORS.PROD.pill
  return ENVIRONMENT_COLORS[getEnvironment()].pill
}

/**
 * The name rendered beside the colour. Returns 'UNKNOWN' rather than getEnvironment()'s 'DEV'
 * fallback when the mode is unrecognized, so the label can never read "DEV" inside a red banner.
 */
export const getEnvironmentLabel = (): string => {
  return isRecognizedMode() ? getEnvironment() : 'UNKNOWN'
}

/**
 * Get the environment prefix for display (e.g., in browser tab)
 * @returns Formatted environment prefix (e.g., [DEV], [STAGING]) or empty string for production
 */
export const getEnvironmentPrefix = (): string => {
  const env = getEnvironment()
  // No prefix in production
  if (env === 'PROD') {
    return ''
  }
  return `[${env}] `
}

/**
 * Set the document title with environment prefix
 * @param title - The page title to set
 */
export const setDocumentTitle = (title: string): void => {
  const prefix = getEnvironmentPrefix()
  document.title = `${prefix}${title}`
}
